import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { useSEO } from '@/hooks/useSEO';
import { getComicById, getRelatedComics } from '@/mocks/comics';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const comicId = parseInt(id || '0', 10);
  const comic = getComicById(comicId);

  const related = comic ? getRelatedComics(comic) : [];

  const notFound = !comic;

  useSEO({
    title: comic
      ? `${comic.title} - 韩国免费漫画在线阅读_漫画之家`
      : '漫画未找到 - 漫画之家',
    description: comic
      ? `在漫画之家免费在线阅读《${comic.title}》(${comic.originalTitle})，作者：${comic.author}。下拉式韩漫阅读体验，第一时间追更。更多热门韩国漫画尽在漫画之家。`
      : '抱歉，您要查看的漫画不存在或已下架。',
    keywords: comic
      ? `${comic.title},韩国漫画,免费韩漫,在线漫画,漫画之家,${comic.author}`
      : '漫画之家,韩国漫画,免费韩漫',
    canonicalUrl: `https://www.manhuazhijia.com/article/${comicId}`,
    ogType: 'article',
    ogImage: comic?.coverImage,
    schemas: comic
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: `${comic.title} - 韩国免费漫画在线阅读`,
            description: comic.description,
            image: comic.coverImage,
            author: { '@type': 'Person', name: comic.author },
            datePublished: comic.updateDate,
            dateModified: comic.updateDate,
            publisher: { '@type': 'Organization', name: '漫画之家' },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `https://www.manhuazhijia.com/article/${comic.id}`,
            },
          },
        ]
      : [],
  });

  if (notFound) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 mb-4">抱歉，您要查看的漫画不存在或已下架。</p>
            <Link
              to="/"
              className="inline-flex bg-primary-500 hover:bg-primary-600 text-white px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <div className="bg-white border-b border-background-200/70">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: t('breadcrumb.home'), href: '/' },
                { label: t('breadcrumb.article') },
                { label: comic.title },
              ]}
            />
          </div>
        </div>

        <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-8">
          <article className="bg-white rounded-lg overflow-hidden">
            <div className="flex flex-col md:flex-row gap-8 p-6 md:p-8">
              <div className="w-full md:w-64 flex-shrink-0">
                <div className="aspect-[3/4] rounded-lg overflow-hidden">
                  <img
                    src={comic.coverImage}
                    alt={`${comic.title} - 韩国免费漫画在线阅读`}
                    title={`${comic.title} - 韩国免费漫画_漫画之家`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                  {comic.title}
                </h1>
                <p className="text-sm text-foreground-500 mb-4">
                  {comic.originalTitle}
                </p>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
                  <div className="bg-background-100 rounded-lg p-3 text-center">
                    <p className="text-xs text-foreground-500 mb-0.5">{t('comic.author')}</p>
                    <p className="text-sm font-semibold text-foreground-900">{comic.author}</p>
                  </div>
                  <div className="bg-background-100 rounded-lg p-3 text-center">
                    <p className="text-xs text-foreground-500 mb-0.5">{t('comic.status')}</p>
                    <p className={`text-sm font-semibold ${comic.status === '连载中' ? 'text-accent-500' : 'text-secondary-500'}`}>
                      {comic.status}
                    </p>
                  </div>
                  <div className="bg-background-100 rounded-lg p-3 text-center">
                    <p className="text-xs text-foreground-500 mb-0.5">{t('comic.rating')}</p>
                    <p className="text-sm font-semibold text-foreground-900 flex items-center justify-center gap-1">
                      <i className="ri-star-fill text-amber-400 text-xs"></i>
                      {comic.rating}
                    </p>
                  </div>
                  <div className="bg-background-100 rounded-lg p-3 text-center">
                    <p className="text-xs text-foreground-500 mb-0.5">{t('comic.views')}</p>
                    <p className="text-sm font-semibold text-foreground-900">
                      {(comic.viewCount / 10000).toFixed(0)}万
                    </p>
                  </div>
                  <div className="bg-background-100 rounded-lg p-3 text-center">
                    <p className="text-xs text-foreground-500 mb-0.5">{t('comic.updateDate')}</p>
                    <p className="text-sm font-semibold text-foreground-900">{comic.updateDate}</p>
                  </div>
                  <div className="bg-background-100 rounded-lg p-3 text-center">
                    <p className="text-xs text-foreground-500 mb-0.5">{t('comic.chapters')}</p>
                    <p className="text-sm font-semibold text-foreground-900">{comic.chapters.length}话</p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-6">
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="bg-secondary-100 text-secondary-700 text-xs px-3 py-1 rounded-full font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="mb-6">
                  <h2 className="text-base font-semibold text-foreground-900 mb-2">
                    {t('comic.description')}
                  </h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {comic.description}
                  </p>
                </div>

                <Link
                  to={`/article/${comic.id}`}
                  className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-book-read-line"></i>
                  {t('comic.readNow')}
                </Link>
              </div>
            </div>

            <div className="border-t border-background-200/70 p-6 md:p-8">
              <h2 className="text-lg font-semibold text-foreground-900 mb-4">
                {t('comic.chapters')} ({comic.chapters.length})
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {comic.chapters.map((chapter) => (
                  <a
                    key={chapter.id}
                    href="#"
                    className="flex items-center justify-between p-3 rounded-lg bg-background-100 hover:bg-primary-100 transition-colors duration-200 group"
                  >
                    <span className="text-sm text-foreground-700 group-hover:text-primary-600 font-medium line-clamp-1">
                      {chapter.title}
                    </span>
                    <span className="text-xs text-foreground-400 whitespace-nowrap ml-2">
                      {chapter.updateDate}
                    </span>
                  </a>
                ))}
              </div>
            </div>
          </article>

          {related.length > 0 && (
            <section className="mt-10">
              <h2 className="text-lg font-semibold text-foreground-900 mb-4">
                {t('comic.relatedTitle')}
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {related.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}